import { canonicalLienStageKeyFromLienRowType } from './lienCreationErrors'

const AMENDED_TYPES = new Set(['amend', 'amended'])
const ORIGINAL_TYPES = new Set(['lien', 'original'])
const DEFAULT_AMEND_BALANCE_THRESHOLD = 499
const ECM_IMPORTED_AMENDED_BALANCE_THRESHOLD = 5000

const LIEN_TYPE_SORT_ORDER = {
    intent: 0,
    lien: 1,
    amend: 2,
    satisfy: 3,
}

function asPositiveThreshold(value, fallback) {
    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed > 0) {
        return parsed
    }

    return fallback
}

function isFilled(value) {
    return value != null && `${value}`.trim() !== ''
}

function isAmendedType(type) {
    return AMENDED_TYPES.has(`${type || ''}`.toLowerCase())
}

/**
 * Resolve the source lien id for an amend (or other) row for a county side.
 * @param {object|null|undefined} row
 * @param {'patient'|'practice'|null|undefined} side
 * @returns {number|null}
 */
export function resolveLienSourceId(row, side) {
    if (!row) {
        return null
    }

    if (side === 'patient') {
        return isFilled(row.patient_source_lien_id) ? Number(row.patient_source_lien_id) : null
    }
    if (side === 'practice') {
        return isFilled(row.practice_source_lien_id) ? Number(row.practice_source_lien_id) : null
    }

    // Combined / same-county: prefer practice, then patient.
    if (isFilled(row.practice_source_lien_id)) {
        return Number(row.practice_source_lien_id)
    }
    if (isFilled(row.patient_source_lien_id)) {
        return Number(row.patient_source_lien_id)
    }
    return null
}

/**
 * Build ancestor path for chain sort: [...ancestorIds, ownId].
 * Walks source pointers; stops on cycles or missing parents.
 * @param {object} row
 * @param {Map<number, object>} byId
 * @param {'patient'|'practice'|null|undefined} side
 * @returns {number[]}
 */
function buildAmendSourceChainPath(row, byId, side) {
    const ownId = Number(row?.id) || 0
    const ancestors = []
    const seen = new Set()
    let currentSourceId = resolveLienSourceId(row, side)

    while (currentSourceId != null && Number.isFinite(currentSourceId) && !seen.has(currentSourceId)) {
        seen.add(currentSourceId)
        ancestors.unshift(currentSourceId)
        const parent = byId.get(currentSourceId)
        if (!parent) {
            break
        }
        currentSourceId = resolveLienSourceId(parent, side)
    }

    return [...ancestors, ownId]
}

function compareNumberArrays(a, b) {
    const len = Math.max(a.length, b.length)
    for (let i = 0; i < len; i += 1) {
        const av = a[i]
        const bv = b[i]
        if (av == null && bv == null) {
            continue
        }
        if (av == null) {
            return -1
        }
        if (bv == null) {
            return 1
        }
        if (av !== bv) {
            return av - bv
        }
    }
    return 0
}

/**
 * Sort lien drawer rows by type (intent → lien → amend → satisfy), then within
 * amend rows by source-lien chain (parent before child). Missing source ids
 * fall back to numeric id order.
 * @param {object[]} items
 * @param {'patient'|'practice'|null|undefined} side
 * @returns {object[]}
 */
export function sortLienItemsByTypeAndSourceChain(items, side) {
    if (!Array.isArray(items) || items.length === 0) {
        return []
    }

    const byId = new Map()
    for (const row of items) {
        const id = Number(row?.id)
        if (Number.isFinite(id)) {
            byId.set(id, row)
        }
    }

    const pathCache = new Map()
    const getPath = (row) => {
        const cacheKey = row?.id
        if (cacheKey != null && pathCache.has(cacheKey)) {
            return pathCache.get(cacheKey)
        }
        const path = isAmendedType(row?.type)
            ? buildAmendSourceChainPath(row, byId, side)
            : [Number(row?.id) || 0]
        if (cacheKey != null) {
            pathCache.set(cacheKey, path)
        }
        return path
    }

    return [...items].sort((a, b) => {
        const aKey = canonicalLienStageKeyFromLienRowType(a?.type)
        const bKey = canonicalLienStageKeyFromLienRowType(b?.type)
        const typeDiff = (LIEN_TYPE_SORT_ORDER[aKey] ?? 99) - (LIEN_TYPE_SORT_ORDER[bKey] ?? 99)
        if (typeDiff !== 0) {
            return typeDiff
        }

        if (isAmendedType(a?.type) && isAmendedType(b?.type)) {
            const pathDiff = compareNumberArrays(getPath(a), getPath(b))
            if (pathDiff !== 0) {
                return pathDiff
            }
        }

        return (Number(a?.id) || 0) - (Number(b?.id) || 0)
    })
}

export function resolveLatestLienByTypes(liens, types) {
    if (!Array.isArray(liens) || !types?.size) {
        return null
    }

    let latest = null
    for (const row of liens) {
        if (!types.has(`${row?.type || ''}`.toLowerCase())) {
            continue
        }
        if (!latest || Number(row.id) > Number(latest.id)) {
            latest = row
        }
    }

    return latest
}

export function resolveLatestAmendLien(liens) {
    return resolveLatestLienByTypes(liens, AMENDED_TYPES)
}

export function lienRowHasRecordedBookPage(lien) {
    if (!lien) {
        return false
    }

    const patientRecorded = isFilled(lien.patient_package_uuid)
        && isFilled(lien.patient_book)
        && isFilled(lien.patient_page)
    const practiceRecorded = isFilled(lien.practice_package_uuid)
        && isFilled(lien.practice_book)
        && isFilled(lien.practice_page)

    return patientRecorded || practiceRecorded
}

export function practiceHasSatisfactionLien(practice) {
    return Array.isArray(practice?.liens)
        && practice.liens.some((row) => `${row?.type || ''}`.toLowerCase() === 'satisfy')
}

export function hasPendingUnrecordedAmend(practice) {
    const latestAmend = resolveLatestAmendLien(practice?.liens)
    if (!latestAmend) {
        return false
    }

    return !lienRowHasRecordedBookPage(latestAmend)
}

export function resolveOriginalLienForAmend(practice) {
    return resolveLatestLienByTypes(practice?.liens, ORIGINAL_TYPES)
}

export function resolveAmendSourceLien(practice) {
    const original = resolveOriginalLienForAmend(practice)
    if (!original || !lienRowHasRecordedBookPage(original)) {
        return null
    }

    if (hasPendingUnrecordedAmend(practice)) {
        return null
    }

    const latestAmend = resolveLatestAmendLien(practice?.liens)
    if (latestAmend && lienRowHasRecordedBookPage(latestAmend)) {
        return latestAmend
    }

    return original
}

export function resolveAmendBalanceBaseline(practice) {
    if (practice?.lien_amount != null && practice.lien_amount !== '') {
        return Number(practice.lien_amount)
    }

    return 0
}

export function isEcmImportedOriginalLien(originalLien) {
    if (!originalLien) {
        return false
    }

    const practiceUuid = `${originalLien.practice_package_uuid ?? ''}`.toLowerCase()
    const patientUuid = `${originalLien.patient_package_uuid ?? ''}`.toLowerCase()

    return practiceUuid.includes('ecm_1_0') || patientUuid.includes('ecm_1_0')
}

export function resolveAmendedLienBalanceThreshold(originalLien, options = {}) {
    const defaultThreshold = asPositiveThreshold(
        options.defaultThreshold,
        DEFAULT_AMEND_BALANCE_THRESHOLD
    )
    const ecmThreshold = asPositiveThreshold(
        options.ecmImportedThreshold,
        ECM_IMPORTED_AMENDED_BALANCE_THRESHOLD
    )

    return isEcmImportedOriginalLien(originalLien) ? ecmThreshold : defaultThreshold
}

export function resolveAmendBalanceDifference(practice) {
    const balance = Number(practice?.balance ?? 0)
    const baseline = resolveAmendBalanceBaseline(practice)

    return balance - baseline
}

/**
 * Matches LienCreation::eligibleAmendedLiensQuery balance gate:
 * positive difference (balance > lien_amount) and above threshold
 * (499 default, 5000 for ECM 1.0 imports).
 */
export function hasAmendBalanceAboveThreshold(practice, originalLien, options = {}) {
    if (!originalLien) {
        return false
    }

    const difference = resolveAmendBalanceDifference(practice)
    if (difference <= 0) {
        return false
    }

    const threshold = resolveAmendedLienBalanceThreshold(originalLien, options)

    return difference > threshold
}

/** Positive balance growth above lien amount, before threshold. */
export function hasPositiveAmendBalanceDifference(practice) {
    return resolveAmendBalanceDifference(practice) > 0
}

/**
 * Whether the Lien tab should show Create Amended for this practice.
 * Hidden only when a satisfaction lien exists; validate/create enforces other rules.
 */
export function canCreateAnotherAmendedLien(practice) {
    return !practiceHasSatisfactionLien(practice)
}

/** @deprecated Use canCreateAnotherAmendedLien */
export function shouldShowCreateAmendedButton(practice) {
    return canCreateAnotherAmendedLien(practice)
}
